/**
 * Lot 355 — Web Vitals tracker (passive, non-intrusive).
 *
 * Mesures collectées : LCP, CLS, INP, TTFB, FCP — sans donnée personnelle,
 * sans contenu de formulaire, sans cookie marketing, sans tracking marketing.
 *
 * Usage : appelé une seule fois au mount de App.js.
 * Les métriques sont envoyées par batch toutes les 5s ou avant pagehide.
 */
import { onLCP, onCLS, onINP, onTTFB, onFCP } from "web-vitals";

const ENDPOINT = "/api/observability/web-vitals";
const FLUSH_INTERVAL_MS = 5000;

let bufferedMetrics = [];
let flushTimer = null;

function detectDeviceType() {
  const ua = (navigator.userAgent || "").toLowerCase();
  const w = window.innerWidth;
  if (/mobile|iphone|ipod|android.*mobile|windows phone/.test(ua)) return "mobile";
  if (/ipad|android(?!.*mobile)|tablet/.test(ua) || (w >= 600 && w <= 1024)) return "tablet";
  return "desktop";
}

function categorizeRoute(pathname) {
  if (!pathname || pathname === "/") return "public_home";
  if (pathname.startsWith("/fr") || pathname.startsWith("/en")) {
    if (pathname.includes("contact")) return "public_contact";
    if (pathname.includes("demande-acces") || pathname.includes("request-access")) return "public_request_access";
    if (pathname.includes("creer-spv") || pathname.includes("create-spv")) return "public_spv_intake";
    if (pathname.match(/(mentions-legales|conditions|politique|avertissement|terms|cookie|privacy|risk)/)) return "public_legal";
    return "public_other";
  }
  if (pathname.startsWith("/login")) return "auth_login";
  if (pathname.startsWith("/activate")) return "auth_activate";
  if (pathname.startsWith("/forgot-password") || pathname.startsWith("/reset-password")) return "auth_recovery";
  if (pathname.startsWith("/portal/")) {
    if (pathname.includes("dashboard")) return "investor_dashboard";
    if (pathname.includes("portfolio")) return "investor_portfolio";
    if (pathname.includes("projects")) return "investor_projects";
    if (pathname.includes("subscriptions")) return "investor_subscriptions";
    if (pathname.includes("documents")) return "investor_documents";
    if (pathname.includes("kyb") || pathname.includes("compliance")) return "investor_compliance";
    return "investor_other";
  }
  if (pathname.startsWith("/admin/")) {
    if (pathname.includes("import")) return "admin_import";
    if (pathname.includes("reports")) return "admin_reports";
    if (pathname.includes("projects")) return "admin_projects";
    if (pathname.includes("investors")) return "admin_investors";
    if (pathname.includes("cockpit") || pathname === "/admin") return "admin_cockpit";
    return "admin_other";
  }
  if (pathname.startsWith("/sponsor/")) return "sponsor";
  return "unknown";
}

function flushNow() {
  if (!bufferedMetrics.length) return;
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  const payload = {
    page_category: categorizeRoute(window.location.pathname),
    route: window.location.pathname,
    device_type: detectDeviceType(),
    build_version: process.env.REACT_APP_VERSION || "",
    metrics: bufferedMetrics.splice(0, bufferedMetrics.length),
  };

  // Use sendBeacon when available (survives pagehide)
  try {
    const url = `${apiBase}${ENDPOINT}`;
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    }
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
      credentials: "include",
    }).catch(() => {
      /* swallow — passive telemetry */
    });
  } catch {
    /* swallow */
  }
}

function onMetric(metric) {
  bufferedMetrics.push({
    name: metric.name,
    value: Math.round(metric.value * 100) / 100,
    id: metric.id,
    navigation_type: metric.navigationType || "",
  });
  if (!flushTimer) {
    flushTimer = setTimeout(() => {
      flushTimer = null;
      flushNow();
    }, FLUSH_INTERVAL_MS);
  }
}

let registered = false;

export function initWebVitals() {
  if (registered) return;
  registered = true;
  try {
    onLCP(onMetric);
    onCLS(onMetric);
    onINP(onMetric);
    onTTFB(onMetric);
    onFCP(onMetric);
    // Flush before page unload to make sure last batch is sent
    window.addEventListener("pagehide", flushNow);
    window.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") flushNow();
    });
  } catch {
    /* web-vitals lib may fail on very old browsers; non-blocking */
  }
}
